// src/components/SRTNetworkAnalyticsCard.tsx
import React, { useState } from 'react';
import {
  Paper,
  Typography,
  List,
  ListItem,
  Divider,
  Box,
  IconButton,
  Popover,
  Tooltip,
} from '@mui/material';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { type StreamStats, type StreamAnalytics } from '../services/streamsService';

interface Props {
  stats: StreamStats;
  analytics: StreamAnalytics;
  isOnline: boolean;
}

const StatItem: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <ListItem sx={{ py: 0.5, px: 0, display: 'flex', justifyContent: 'space-between' }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      color="text.primary"
      sx={{ wordBreak: 'break-all', textAlign: 'right' }}
    >
      {value ?? 'N/A'}
    </Typography>
  </ListItem>
);

const formatDuration = (totalSeconds: number): string => {
  if (isNaN(totalSeconds) || totalSeconds < 0) return '00:00:00';
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const pad = (num: number) => num.toString().padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

export const SRTNetworkAnalyticsCard: React.FC<Props> = ({ stats, analytics, isOnline }) => {
  const statusColor = isOnline ? 'success.main' : 'error.main';
  const receiverLoss = stats.packetsReceivedLoss ?? 0;
  const receiverTotal = stats.packetsReceived ?? 0;

  const receiverLossRate =
    receiverTotal > 0 ? Math.round((receiverLoss / receiverTotal) * 100 * 100) / 100 : 0;

  const isHighLoss = receiverLossRate > 3;
  const lossStyle = { color: isHighLoss ? 'red' : 'inherit', fontWeight: isHighLoss ? 600 : 400 };
  const lossStatus = isHighLoss ? '. High loss rate.' : '';

  const formattedPacketLoss = `${receiverLoss} / ${receiverTotal}`;
  const bufferLatency = stats.msReceiveTsbPdDelay ? `${stats.msReceiveTsbPdDelay} ms` : 'N/A';
  const formattedBitrate = analytics.bitrateKbps
    ? `${(analytics.bitrateKbps / 1000).toFixed(2)} Mbps`
    : '0';
  const startTime = analytics.startTime
    ? new Date(analytics.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : 'N/A';
  const formattedDuration = formatDuration(analytics.uptimeSec);

  // --- Mobile-friendly question mark popover ---
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const handleOpen = (event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  return (
    <Paper
      elevation={3}
      sx={{ borderRadius: 0, p: 2, borderLeft: `4px solid`, borderColor: statusColor }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <NetworkCheckIcon sx={{ fontSize: '1.25rem', mr: 1, color: 'success.main' }} />
        <Typography variant="h5">SRT Analytics</Typography>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <List dense>
        <StatItem label="Bitrate" value={formattedBitrate} />
        <StatItem label="Start Time" value={startTime} />
        <StatItem label="Duration" value={formattedDuration} />
        <StatItem label="Latency (RTT)" value={stats.msRTT ? `${stats.msRTT.toFixed(2)} ms` : 'N/A'} />
        <StatItem label="Buffer / Latency" value={bufferLatency} />
        <StatItem label="Packet Loss" value={formattedPacketLoss} />
        <StatItem
          label="Loss Rate"
          value={
            isHighLoss ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <Typography component="span" sx={lossStyle}>
                  {receiverLossRate}%{lossStatus}
                </Typography>
                <Tooltip title="Tap for advice" arrow>
                  <IconButton
                    size="small"
                    onClick={handleOpen}
                    sx={{ ml: 0.5, p: 0.2 }}
                    //color="error"
                  >
                    <HelpOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Popover
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                >
                  <Box sx={{ p: 1.5, maxWidth: 220 }}>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      If sustained, either reboot your router and/or machine, or adjust your stream settings, or call your ISP.
                    </Typography>
                  </Box>
                </Popover>
              </Box>
            ) : (
              <span style={lossStyle}>{receiverLossRate}%</span>
            )
          }
        />
        <StatItem label="Remote Address" value={stats.remoteAddr} />
      </List>
    </Paper>
  );
};
